import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../../shared/Icon';
import { speakNorwegian } from '../../../shared/speech';
import type { ExercisePluginProps } from '../types';
import { strings, text } from '../contentFields';

export function ChoiceExercise({ exercise, onComplete, showToast }: ExercisePluginProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [introduced, setIntroduced] = useState(exercise.content.showTeaching !== true);
  const continueButton = useRef<HTMLButtonElement>(null);
  const listening = exercise.type === 'listening';
  const audio = exercise.content.audio as { transcript?: string } | undefined;
  const options = strings(exercise.content, 'options');
  const context = text(exercise.content, 'context');
  const correctIndex = Number(exercise.evaluation.correctIndex ?? -1);
  const correct = selected === correctIndex;
  const verbPhraseContextChoice = exercise.content.verbPhraseContextChoice === true;

  useEffect(() => {
    if (!submitted) return;
    const frame = window.requestAnimationFrame(() => continueButton.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }));
    return () => window.cancelAnimationFrame(frame);
  }, [submitted]);

  function play(value = audio?.transcript ?? '') {
    if (!speakNorwegian(value)) showToast('Lyd er ikke tilgjengelig i denne nettleseren.');
  }

  async function finish() {
    if (selected === null) return;
    if (!submitted) return setSubmitted(true);
    setSaving(true);
    await onComplete({
      response: { selectedIndex: selected },
      result: { correct, selectedIndex: selected, correctIndex },
      evidence: exercise.targets.map(itemId => ({ itemId, dimension: 'recognized', value: correct ? 0.8 : 0.2 }))
    });
  }

  if (verbPhraseContextChoice && !introduced) {
    const canonicalForm = text(exercise.content, 'canonicalForm');
    const teachingExample = text(exercise.content, 'teachingExample');
    return <>
      <p className="exercise-kicker">MØT UTTRYKKET FØRST</p>
      <h2 className="exercise-title">{canonicalForm}</h2>
      <p className="exercise-intro">Les betydningen og ett eksempel. Deretter prøver du uten å se dette kortet.</p>
      <article className="verb-teaching-card">
        <div><span>BETYR HER</span><p>{text(exercise.content, 'meaning')}</p></div>
        <blockquote>«{teachingExample}»</blockquote>
        <button className="listen-button" onClick={() => play(teachingExample)}><Icon name="volume" /> Hør eksemplet</button>
      </article>
      <div className="speak-actions"><button className="exercise-next" onClick={() => setIntroduced(true)}>Prøv uten eksemplet <Icon name="arrow" /></button></div>
    </>;
  }

  return <>
    <p className="exercise-kicker">{listening ? 'LYTT UTEN Å OVERSETTE' : verbPhraseContextChoice ? 'VELG SAMME MENING' : 'GJENKJENN BETYDNINGEN'}</p>
    <h2 className="exercise-title">{listening ? 'Hva ber personen om?' : verbPhraseContextChoice ? 'Hvilken setning betyr det samme?' : exercise.content.verbPhrase ? 'Hvilken betydning passer?' : context ? 'Forstå ordet i sammenhengen.' : exercise.content.lemma ? `Hva betyr «${String(exercise.content.lemma)}»?` : 'Velg situasjonen som passer.'}</h2>
    <p className="exercise-intro">{text(exercise.content, 'instruction')}</p>
    {listening && <div className="flashcard compact-flashcard"><button className="mic-button" onClick={() => play()}><Icon name="volume" /> Spill av replikk</button>{submitted && <p>«{audio?.transcript}»</p>}</div>}
    {context && <div className="prompt-card"><span className="prompt-label">{verbPhraseContextChoice ? 'NØYTRAL SETNING' : 'KONTEKST'}</span><blockquote>«{context}»</blockquote></div>}
    <fieldset className="choice-list">
      <legend>{text(exercise.content, listening ? 'question' : 'prompt')}</legend>
      {options.map((option, index) => <button type="button" key={option} className={submitted ? index === correctIndex ? 'correct' : index === selected ? 'incorrect' : '' : selected === index ? 'selected' : ''} disabled={submitted} onClick={() => setSelected(index)}>{option}</button>)}
    </fieldset>
    {submitted && <div className={`feedback-box ${correct ? '' : 'evaluation-pending'}`}>
      <h4>{correct ? String(exercise.feedback.success ?? 'Riktig.') : 'Ikke helt.'}</h4>
      <p>{String(exercise.feedback.explanation ?? '')}</p>
      {!correct && <p>{String(exercise.feedback.retryInstruction)}</p>}
    </div>}
    <div className="speak-actions"><button ref={continueButton} className="exercise-next" disabled={selected === null || saving} onClick={finish}>{submitted ? 'Fortsett' : 'Sjekk svaret'} <Icon name="arrow" /></button></div>
  </>;
}
